import * as React from 'react';
import { BREAKPOINT } from '@/lib/tokens';

const MOBILE_BREAKPOINT = 768;

/**
 * Returns whether the current viewport width is below the mobile breakpoint (768px).
 *
 * Uses `matchMedia` to listen for viewport changes reactively.
 *
 * @returns `true` if the viewport is narrower than 768px, `false` otherwise.
 */
export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener('change', onChange);
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return !!isMobile;
}

export type BreakpointName = 'mobile' | 'tablet' | 'desktop';

function getBreakpoint(width: number): BreakpointName {
  if (width >= BREAKPOINT.desktop) return 'desktop';
  if (width >= BREAKPOINT.tablet) return 'tablet';
  return 'mobile';
}

/**
 * Returns the current named breakpoint based on viewport width.
 *
 * Uses `matchMedia` listeners on the tablet and desktop thresholds (from
 * design tokens) to update reactively on resize.
 *
 * @returns The active {@link BreakpointName}: `'mobile'`, `'tablet'`, or `'desktop'`.
 */
export function useBreakpoint(): BreakpointName {
  const [bp, setBp] = React.useState<BreakpointName>(() =>
    typeof window !== 'undefined' ? getBreakpoint(window.innerWidth) : 'mobile',
  );

  React.useEffect(() => {
    const tabletMql = window.matchMedia(`(min-width: ${BREAKPOINT.tablet}px)`);
    const desktopMql = window.matchMedia(`(min-width: ${BREAKPOINT.desktop}px)`);
    const update = () => setBp(getBreakpoint(window.innerWidth));
    tabletMql.addEventListener('change', update);
    desktopMql.addEventListener('change', update);
    update();
    return () => {
      tabletMql.removeEventListener('change', update);
      desktopMql.removeEventListener('change', update);
    };
  }, []);

  return bp;
}
